import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { prepareAssets } from 'apis/asset-repository-service/api';
import { AssetScope } from 'apis/asset-repository-service/types';
import useUser from 'common/hooks/useUser';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';

import { FileUpload, concurrentFilesUploader } from './concurrentFilesUploader';

type UploadProgress = { count: number; total: number };

const INITIAL_PROGRESS: UploadProgress = {
  count: 0,
  total: 0,
};

const isAssetScope = (scope: string): scope is AssetScope['type'] =>
  ['venue', 'merchant', 'brand', 'country', 'unspecified'].includes(scope);

const useAssetRepositoryUpload = () => {
  const { isEmployee, user } = useUser();
  const scope = useScopeContext();
  const [progress, setUploadProgress] = useState<UploadProgress>(INITIAL_PROGRESS);

  const { mutateAsync: prepareAssetsMutation, isLoading } = useMutation(
    async (files: File[]) => {
      if (!user) {
        throw new Error('User must be authorized to upload assets');
      }

      setUploadProgress({ count: 0, total: files.length });

      const preparedAssets = await prepareAssets(
        files.map(file => ({
          contentType: file.type,
          name: file.name,
          scope: {
            type: isAssetScope(scope.type) ? scope.type : 'unspecified',
            scopeId: scope.id,
          },
          source: { type: isEmployee ? 'wolt_employee' : 'self_service', sourceId: user.id },
        })),
      );

      const publishedImagesUrls: string[] = [];

      const uploads: FileUpload[] = preparedAssets.map((preparedAsset, index) => ({
        file: files[index],
        uploadUrl: preparedAsset.s3PresignedUrl,
        onUploadFinished: () => {
          publishedImagesUrls.push(preparedAsset.imageResizerProxyUrl);
          setUploadProgress(progress => ({ ...progress, count: progress.count + 1 }));
        },
      }));

      const uploadErrors = await concurrentFilesUploader(uploads);

      return { publishedImagesUrls, uploadErrors };
    },
    {
      retry: 1,
    },
  );

  return {
    uploadAssets: prepareAssetsMutation,
    isLoading,
    progress,
  };
};

export default useAssetRepositoryUpload;
